
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

export interface Supplier {
  id: string;
  business_name: string;
  contact_person_name: string;
  contact_person_phone: string;
  contact_person_email: string;
  pan: string;
  gst: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  category: string;
  punctuality_rating: number;
  quality_rating: number;
  tech_capability_rating: number;
  pricing_rating: number;
  overall_rating: number;
  bank_account_number: string;
  bank_ifsc: string;
  bank_name: string;
  established_year: number;
  annual_turnover: number;
  specialization: string;
  certifications: string[];
  payment_terms: string;
  credit_limit: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SupplierOrder {
  id: string;
  supplier_id: string;
  order_number: string;
  order_date: string;
  expected_delivery_date: string;
  actual_delivery_date?: string;
  status: string;
  total_amount: number;
  gold_rate_fixed: boolean;
  fixed_gold_rate?: number;
  payment_status: string;
  notes?: string;
  supplier?: Supplier;
  items?: SupplierOrderItem[];
}

export interface SupplierOrderItem {
  id: string;
  order_id: string;
  product_code: string;
  product_name: string;
  category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  gold_weight: number;
  purity: string;
  making_charges: number;
  stone_details?: string;
  specifications?: string;
  qc_status: string;
  qc_notes?: string;
  qc_date?: string;
  qc_by?: string;
  delivery_status: string;
  is_delayed: boolean;
  delay_reason?: string;
}

export interface SupplierLedgerEntry {
  id: string;
  supplier_id: string;
  transaction_type: string;
  reference_id?: string;
  reference_type?: string;
  debit_amount: number;
  credit_amount: number;
  balance: number;
  description: string;
  transaction_date: string;
}

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('business_name');

      if (error) throw error;
      setSuppliers(data || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createSupplier = async (supplierData: Partial<Supplier>) => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .insert([supplierData])
        .select()
        .single();

      if (error) throw error;
      setSuppliers(prev => [...prev, data]);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateSupplier = async (id: string, updates: Partial<Supplier>) => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setSuppliers(prev => prev.map(s => s.id === id ? data : s));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteSupplier = async (id: string) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuppliers(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return {
    suppliers,
    loading,
    error,
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier
  };
};

export const useSupplierOrders = () => {
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('supplier_orders')
        .select(`
          *,
          supplier:suppliers(*),
          items:supplier_order_items(*)
        `)
        .order('order_date', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (orderData: Partial<SupplierOrder>) => {
    try {
      const { data, error } = await supabase
        .from('supplier_orders')
        .insert([orderData])
        .select()
        .single();

      if (error) throw error;
      await fetchOrders(); // Refresh to get full data with relations
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateOrderStatus = async (id: string, status: string) => {
    try {
      const { data, error } = await supabase
        .from('supplier_orders')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateItemQCStatus = async (itemId: string, qcStatus: string, qcNotes?: string) => {
    try {
      const { data, error } = await supabase
        .from('supplier_order_items')
        .update({ 
          qc_status: qcStatus,
          qc_notes: qcNotes,
          qc_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      await fetchOrders(); // Refresh to get updated data
      return data;
    } catch (err) {
      throw err;
    }
  };

  return {
    orders,
    loading,
    error,
    fetchOrders,
    createOrder,
    updateOrderStatus,
    updateItemQCStatus
  };
};

export const useSupplierLedger = () => {
  const [ledgerEntries, setLedgerEntries] = useState<SupplierLedgerEntry[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchLedgerData();
  }, []);

  const fetchLedgerData = async () => {
    try {
      setLoading(true);
      
      // Fetch ledger entries
      const { data: ledgerData, error: ledgerError } = await supabase
        .from('supplier_ledger')
        .select('*')
        .order('transaction_date', { ascending: false });

      if (ledgerError) throw ledgerError;

      // Fetch suppliers
      const { data: suppliersData, error: suppliersError } = await supabase
        .from('suppliers')
        .select('*')
        .order('business_name');

      if (suppliersError) throw suppliersError;

      setLedgerEntries(ledgerData || []);
      setSuppliers(suppliersData || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const addLedgerEntry = async (entryData: Partial<SupplierLedgerEntry>) => {
    try {
      // Calculate new balance
      const supplierEntries = ledgerEntries.filter(e => e.supplier_id === entryData.supplier_id);
      const currentBalance = supplierEntries.reduce((balance, entry) => {
        return balance + (entry.credit_amount || 0) - (entry.debit_amount || 0);
      }, 0);
      
      const newBalance = currentBalance + (entryData.credit_amount || 0) - (entryData.debit_amount || 0);

      const { data, error } = await supabase
        .from('supplier_ledger')
        .insert([{ ...entryData, balance: newBalance }])
        .select()
        .single();

      if (error) throw error;
      setLedgerEntries(prev => [data, ...prev]);
      return data;
    } catch (err) {
      throw err;
    }
  };

  return {
    ledgerEntries,
    suppliers,
    loading,
    error,
    fetchLedgerData,
    addLedgerEntry
  };
};
